/* eslint-env es2020 */
// zpost-app/src/components/ensapp/AddressLinker.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { JsonRpcProvider, namehash, getAddress } from 'ethers';
import { useAuth } from '../../contexts/AuthContext';
import useBridgeTransaction from '../../hooks/useBridgeTransaction';
import { getRPCProvider } from '../../utils/rpc';

const PUBLIC_RESOLVER_ADDRESS = '0x231b0Ee14048e9dCcD1d247744d114a4EB5E8E63';

const ZPOST_ORIGIN = 'https://zpost.ai';

const supportedChains = [
  { key: 'LTC', name: 'Litecoin', placeholder: 'LYtF...', pattern: /^[LM3][a-km-zA-HJ-NP-Z1-9]{26,33}$/, description: 'Litecoin mainnet address' },
  { key: 'DOGE', name: 'Dogecoin', placeholder: 'D9Cw...', pattern: /^D{1}[5-9A-HJ-NP-U]{1}[1-9A-HJ-NP-Za-km-z]{32}$/, description: 'Dogecoin mainnet address' },
  { key: 'SOL', name: 'Solana', placeholder: '9WzD...', pattern: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/, description: 'Solana mainnet address' },
  { key: 'AVAX', name: 'Avalanche', placeholder: 'X-avax1...', pattern: /^X-avax1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{38}$/, description: 'Avalanche X-Chain address' },
  { key: 'MATIC', name: 'Polygon', placeholder: '0x742d...', pattern: /^0x[a-fA-F0-9]{40}$/, description: 'Polygon mainnet address' },
  { key: 'ARB', name: 'Arbitrum', placeholder: '0x742d...', pattern: /^0x[a-fA-F0-9]{40}$/, description: 'Arbitrum mainnet address' },
  { key: 'OP', name: 'Optimism', placeholder: '0x742d...', pattern: /^0x[a-fA-F0-9]{40}$/, description: 'Optimism mainnet address' },
  { key: 'ATOM', name: 'Cosmos', placeholder: 'cosmos1...', pattern: /^cosmos1[a-z0-9]{38}$/, description: 'Cosmos Hub address' }
];

const coinTypes = {
  'LTC': 2,
  'DOGE': 3,
  'SOL': 501,
  'AVAX': 9000,
  'MATIC': 966,
  'ARB': 42161,
  'OP': 10,
  'ATOM': 118
};

const AddressLinker = ({ identityAuth }) => {
  const { walletAddress, ensName, isAuthenticated } = identityAuth || {};

  const [isSetting, setIsSetting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [transactionStatus, setTransactionStatus] = useState('');
  const [currentRecords, setCurrentRecords] = useState({});

  // RPC provider
  const [stableProvider, setStableProvider] = useState(null);

  // Hooks
  const { wallet: authWallet, connectToZwallet } = useAuth();
  const { sendTransaction: bridgeSend } = useBridgeTransaction();

  // Receipt modal state
  const [receiptTxHash, setReceiptTxHash] = useState(null);

  // Ref to keep authWallet up‑to‑date in async callbacks
  const authWalletRef = useRef(authWallet);
  useEffect(() => {
    authWalletRef.current = authWallet;
    console.log('authWalletRef updated:', authWallet?.isConnected);
  }, [authWallet]);

  // Transaction deduplication
  const processedTransactions = useRef(new Set());

  // Initialize provider on mount
  useEffect(() => {
    let cancelled = false;
    const initProvider = async () => {
      try {
        const provider = await getRPCProvider();
        if (!cancelled) setStableProvider(provider);
      } catch (err) {
        console.error('Failed to initialize RPC provider:', err);
        setError('Could not connect to Ethereum network. Please refresh.');
      }
    };
    initProvider();
    return () => { cancelled = true; };
  }, []);

  // -------------------------
  // normalizeTx helper (kept from original)
  // -------------------------
  const normalizeTx = useCallback((tx) => {
    if (!tx || typeof tx !== 'object') return tx;
    const out = { ...tx };

    if (out.to && typeof out.to !== 'string') {
      try { out.to = String(out.to); } catch (e) { /* ignore */ }
    }

    try { if (out.to) out.to = getAddress(out.to); } catch (e) { /* ignore */ }

    if (out.data && typeof out.data === 'string' && !out.data.startsWith('0x')) {
      out.data = '0x' + out.data;
    }

    const v = out.value;
    if (v === undefined || v === null || v === '') {
      out.value = '0x0';
    } else if (typeof v === 'string') {
      const s = v.trim();
      if (s.startsWith('0x') || s.startsWith('0X')) {
        out.value = s;
      } else if (s.indexOf('.') >= 0) {
        const n = Number(s);
        if (!isNaN(n)) {
          try { 
            out.value = '0x' + BigInt(Math.round(n * 1e18)).toString(16); 
          } catch (e) { out.value = '0x0'; }
        } else {
          try { 
            out.value = '0x' + BigInt(s).toString(16); 
          } catch (e) { out.value = '0x0'; }
        }
      } else {
        try { 
          out.value = '0x' + BigInt(s).toString(16); 
        } catch (e) { out.value = '0x0'; }
      }
    } else if (typeof v === 'number') {
      try { 
        out.value = '0x' + BigInt(Math.floor(v)).toString(16); 
      } catch (e) { out.value = '0x0'; }
    } else if (typeof v === 'bigint') {
      out.value = '0x' + v.toString(16);
    } else {
      out.value = '0x0';
    }

    if (!out.chainId) out.chainId = '0x1';
    else if (typeof out.chainId === 'number') out.chainId = '0x' + Number(out.chainId).toString(16);
    else if (typeof out.chainId === 'string' && !out.chainId.startsWith('0x')) {
      try { 
        out.chainId = '0x' + BigInt(out.chainId).toString(16); 
      } catch (e) { out.chainId = '0x1'; }
    }

    if (!out.from && walletAddress) out.from = walletAddress;
    if (out.from) out.from = String(out.from);

    if (!out.gasLimit && !out.gas) out.gasLimit = '0x186a0';
    if (!out.gasPrice && !out.maxFeePerGas && !out.maxPriorityFeePerGas) {
      out.gasPrice = '0x2540be400';
    }

    return out;
  }, [walletAddress]);

  // -------------------------
  // Fetch current records (using stableProvider)
  // -------------------------
  const fetchCurrentRecords = useCallback(async () => {
    if (!ensName || !stableProvider) {
      setCurrentRecords({});
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const resolver = await stableProvider.getResolver(ensName);
      if (!resolver) {
        setCurrentRecords({});
        return;
      }

      const records = {};
      for (const chain of supportedChains) {
        const coinType = coinTypes[chain.key];
        try {
          const addr = await resolver.getAddress(coinType);
          if (addr && addr !== '0x0000000000000000000000000000000000000000') {
            records[chain.key] = addr;
          }
        } catch (e) {
          console.debug(`[AddressLinker] No ${chain.key} address found`);
        }
      }

      setCurrentRecords(records);
    } catch (err) {
      console.error('Failed to fetch current records:', err);
      setError('Failed to fetch current address records');
    } finally {
      setIsLoading(false);
    }
  }, [ensName, stableProvider]);

  // -------------------------
  // Build transaction payload (using stableProvider)
  // -------------------------
  const buildAddressTxPayload = useCallback(async (chainKey, address) => {
    if (!ensName) throw new Error('No ENS name selected');
    if (!stableProvider) throw new Error('Network not ready');

    const coinType = coinTypes[chainKey];
    const node = namehash(ensName);
    
    // Use public resolver for address records
    const resolverAddress = PUBLIC_RESOLVER_ADDRESS;
    console.log(`🔧 Using public resolver for address record: ${resolverAddress} for domain: ${ensName}`);

    try {
      const code = await stableProvider.getCode(resolverAddress);
      if (!code || code === '0x') {
        throw new Error(`Resolver contract not found at address ${resolverAddress}`);
      }
    } catch (codeErr) {
      throw new Error(`Resolver check failed: ${codeErr?.message || String(codeErr)}`);
    }

    const iface = new (await import('ethers')).Interface([
      "function setAddr(bytes32 node, uint256 coinType, bytes memory a) external"
    ]);

    // For EVM chains, address should be 20 bytes (40 hex chars without 0x)
    // For non-EVM chains, we need to handle encoding appropriately
    let addressBytes;
    if (chainKey === 'MATIC' || chainKey === 'ARB' || chainKey === 'OP') {
      const cleanAddr = address.replace('0x', '');
      if (cleanAddr.length !== 40) {
        throw new Error(`Invalid EVM address length: ${cleanAddr.length} (expected 40 hex chars)`);
      }
      addressBytes = '0x' + cleanAddr.padStart(64, '0'); // pad to 32 bytes
    } else {
      // For non-EVM chains, use the address as bytes (simplified)
      addressBytes = '0x' + Buffer.from(address).toString('hex');
    }

    const data = iface.encodeFunctionData('setAddr', [node, coinType, addressBytes]);
    
    const txPayload = {
      to: resolverAddress,
      data,
      value: '0x0'
    };

    return { 
      txPayload, 
      display: {
        title: `Set ${supportedChains.find(c => c.key === chainKey)?.name} Address for ${ensName}`,
        description: `Setting ${chainKey} address to: ${address.slice(0, 20)}...`,
        type: 'ens_address'
      }
    };
  }, [ensName, stableProvider]);

  // -------------------------
  // sendSingleTransaction – simplified, uses bridge
  // -------------------------
  const sendSingleTransaction = useCallback(async (chainKey, address) => {
    if (!ensName) throw new Error('No ENS domain selected');
    if (!walletAddress) throw new Error('Wallet not connected');
    if (!stableProvider) throw new Error('Network not ready');

    // Transaction deduplication
    const txKey = `${ensName}-${chainKey}-${address}-${Date.now()}`;
    if (processedTransactions.current.has(txKey)) {
      console.warn('[sendSingleTransaction] Duplicate transaction detected, skipping');
      return { success: true, duplicate: true };
    }
    processedTransactions.current.add(txKey);
    
    if (processedTransactions.current.size > 10) {
      const array = Array.from(processedTransactions.current);
      processedTransactions.current = new Set(array.slice(-10));
    }

    console.log(`🔄 [sendSingleTransaction] Starting transaction for ${ensName}`, {
      chainKey,
      address,
      walletAddress,
      timestamp: new Date().toISOString()
    });

    // Domain ownership verification
    try {
      console.log(`🔍 Verifying ownership of ${ensName} for ${walletAddress}`);
      
      const resolvedAddress = await stableProvider.resolveName(ensName);
      if (!resolvedAddress || resolvedAddress.toLowerCase() !== walletAddress.toLowerCase()) {
        throw new Error(`Domain ${ensName} does not resolve to your wallet address. It resolves to: ${resolvedAddress || 'nothing'}`);
      }

      const resolver = await stableProvider.getResolver(ensName);
      if (!resolver) {
        throw new Error(`No resolver configured for ${ensName}`);
      }

      console.log(`✅ Domain ownership verified: ${ensName} -> ${walletAddress}`);
    } catch (verifyErr) {
      console.error('Domain ownership verification failed:', verifyErr);
      setIsSetting(false);
      setTransactionStatus('');
      setError(`Cannot set address record: ${verifyErr.message}`);
      throw verifyErr;
    }

    setIsSetting(true);
    setTransactionStatus('preparing');
    setError('');
    setSuccess('');

    let txPayload;
    try {
      ({ txPayload } = await buildAddressTxPayload(chainKey, address));
    } catch (buildErr) {
      console.error('[sendSingleTransaction] Failed to build/prepare tx:', buildErr);
      setIsSetting(false);
      setTransactionStatus('');
      setError(buildErr?.message || String(buildErr));
      throw buildErr;
    }

    const normalizedTxPayload = (() => {
      try {
        const n = normalizeTx(txPayload || {});
        if (!n.chainId) n.chainId = '0x1';
        if (!n.from && walletAddress) n.from = walletAddress;
        if (n.to) try { n.to = String(n.to); } catch (e) {}
        return n;
      } catch (e) {
        console.debug('[sendSingleTransaction] normalization fallback failed', e);
        return txPayload || {};
      }
    })();

    // Pre‑flight gas check (optional)
    let preflightGasEstimate = null;
    try {
      preflightGasEstimate = await stableProvider.estimateGas({
        to: normalizedTxPayload.to,
        data: normalizedTxPayload.data,
        value: normalizedTxPayload.value || '0x0',
        from: normalizedTxPayload.from || walletAddress
      });
    } catch (preErr) {
      console.warn('[preflight] gas estimation failed (continuing):', preErr);
    }

    const tx = {
      to: normalizedTxPayload.to,
      data: normalizedTxPayload.data,
      value: normalizedTxPayload.value || '0x0',
      from: normalizedTxPayload.from || walletAddress,
      ...(preflightGasEstimate ? { gasLimit: preflightGasEstimate } : {})
    };

    // Send via bridge
    try {
      console.log('🔑 Using bridge transaction');
      const result = await bridgeSend(tx);
      console.log('✅ Transaction confirmed:', result);

      // Show receipt modal
      setReceiptTxHash({
        hash: result.hash,
        blockNumber: result.blockNumber
      });

      setIsSetting(false);
      setTransactionStatus('confirmed');
      setSuccess(result.hash ? `Transaction confirmed: ${result.hash.slice(0,10)}...` : 'Transaction processed');
      await fetchCurrentRecords();
      return { success: true, hash: result.hash };
    } catch (err) {
      console.error('Transaction failed:', err);
      setIsSetting(false);
      setTransactionStatus('failed');
      setError(err.message || 'Transaction failed');
      throw err;
    }
  }, [ensName, walletAddress, stableProvider, normalizeTx, bridgeSend, buildAddressTxPayload, fetchCurrentRecords]);

  const setChainAddress = useCallback(async (chainKey, address) => {
    const chain = supportedChains.find(c => c.key === chainKey);
    if (chain && !chain.pattern.test(address)) {
      setError(`Invalid ${chain.name} address format`);
      return;
    }

    // If wallet not connected, open unlock modal and wait
    if (!authWalletRef.current?.isConnected) {
      console.log('Wallet not connected, opening wallet modal via connectToZwallet');
      connectToZwallet();

      let walletConnected = false;
      console.log('⏳ Waiting for wallet to connect (max 300 seconds)...');
      for (let attempt = 0; attempt < 300; attempt++) {
        if (authWalletRef.current?.isConnected) {
          walletConnected = true;
          console.log(`✅ Wallet connected after ${attempt} seconds`);
          break;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      if (!walletConnected) {
        setError('Wallet not ready. Please unlock your wallet and try again.');
        return;
      }
    }

    setError('');
    setSuccess('');
    try {
      await sendSingleTransaction(chainKey, address);
    } catch (err) {
      setError(err?.message || `Failed to set ${chainKey} address`);
    }
  }, [sendSingleTransaction, connectToZwallet, authWalletRef]);

  // Initialization
  useEffect(() => {
    if (stableProvider && ensName) {
      fetchCurrentRecords();
    } else {
      setIsLoading(false);
    }
  }, [stableProvider, ensName, fetchCurrentRecords]);

  const refreshRecords = () => {
    fetchCurrentRecords();
    setSuccess('Address records refreshed!');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (isLoading || !stableProvider) {
    return (
      <div className="address-linker-container">
        <h2>Multi-Chain Address Linker</h2>
        <div className="identity-card" style={{ textAlign: 'center', padding: '3rem', background: 'var(--black)', border: '1px solid var(--gold)' }}>
          <p style={{ color: 'var(--gold)' }}>Loading your address records...</p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', marginTop: '1rem' }}>
            Connecting to Ethereum network...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="address-linker-container">
      <h2>Multi-Chain Address Linker</h2>

      <div className="address-linker-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
              Link your cryptocurrency addresses from different chains to your ENS name.
              Dapps can then discover all your addresses automatically.
            </p>
            {ensName && (
              <p style={{ color: 'var(--gold)', fontSize: '0.9rem', margin: '0.5rem 0 0 0' }}>
                Configuring addresses for: <strong>{ensName}</strong>
              </p>
            )}
          </div>
          <button onClick={refreshRecords} className="btn btn-gold" style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}>
            Refresh
          </button>
        </div>

        {transactionStatus && (
          <div style={{ 
            background: 'var(--black)', 
            border: '1px solid var(--gold)', 
            padding: '12px', 
            borderRadius: '8px', 
            marginBottom: '16px' 
          }}>
            <p style={{ margin: 0, fontWeight: 'bold', color: 'var(--gold)' }}>
              Transaction Status: {transactionStatus}
            </p>
          </div>
        )}

        {success && <div className="status-message status-success">{success}</div>}
        {error && <div className="status-message status-error">{error}</div>}

        <div className="chains-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' }}>
          {supportedChains.map((chain) => (
            <ChainAddressForm
              key={chain.key}
              chain={chain}
              currentAddress={currentRecords[chain.key] || ''}
              isSetting={isSetting}
              onSetAddress={setChainAddress}
              ensName={ensName}
              derivedWalletReady={authWalletRef.current?.isConnected}
            />
          ))}
        </div>

        {!ensName && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <p style={{ color: 'var(--text-secondary)' }}>
              Please connect your wallet and have an ENS name to manage multi-chain addresses.
            </p>
          </div>
        )}

        <div style={{ marginTop: '2rem', padding: '1rem', background: 'rgba(255, 215, 0, 0.1)', borderRadius: '8px', border: '1px solid var(--gold)' }}>
          <h4 style={{ color: 'var(--gold)', marginBottom: '0.5rem' }}>About Multi-Chain Addresses</h4>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
            Linking addresses from different chains to your ENS name allows dapps to discover all your cryptocurrency
            addresses automatically. This enables seamless cross-chain interactions and unified identity management.
          </p>
        </div>
      </div>

      {/* Receipt Modal */}
      {receiptTxHash && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.95)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--card-bg)',
            padding: '30px',
            borderRadius: '15px',
            border: '2px solid var(--gold)',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 0 50px rgba(255,215,0,0.3)'
          }}>
            <h3 style={{ color: 'var(--gold)', margin: '0 0 20px 0', fontSize: '24px', textAlign: 'center' }}>
              Transaction Confirmed
            </h3>
            <div style={{
              background: 'var(--black)',
              padding: '20px',
              borderRadius: '10px',
              border: '1px solid var(--gold)',
              marginBottom: '20px'
            }}>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '15px' }}>
                Your transaction has been successfully mined.
              </p>
              {receiptTxHash.blockNumber && (
                <div style={{ marginBottom: '10px' }}>
                  <strong style={{ color: 'var(--gold)' }}>Block:</strong>
                  <span style={{ color: 'var(--text-light)', marginLeft: '5px' }}>
                    {receiptTxHash.blockNumber}
                  </span>
                </div>
              )}
              <div style={{
                background: 'rgba(255,215,0,0.1)',
                padding: '10px',
                borderRadius: '5px',
                wordBreak: 'break-all',
                marginBottom: '15px'
              }}>
                <a
                  href={`https://etherscan.io/tx/${receiptTxHash.hash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--gold)', textDecoration: 'underline' }}
                >
                  {receiptTxHash.hash}
                </a>
              </div>
              <a
                href={`https://etherscan.io/tx/${receiptTxHash.hash}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'block',
                  textAlign: 'center',
                  background: 'var(--gold)',
                  color: 'var(--black)',
                  padding: '10px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  marginBottom: '10px'
                }}
              >
                View on Etherscan
              </a>
            </div>
            <button
              onClick={() => setReceiptTxHash(null)}
              style={{
                width: '100%',
                background: 'transparent',
                color: '#ff4444',
                border: '1px solid #ff4444',
                padding: '15px',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Chain form component (unchanged)
const ChainAddressForm = ({ chain, currentAddress, isSetting, onSetAddress, ensName, derivedWalletReady }) => {
  const [address, setAddress] = useState(currentAddress || '');

  useEffect(() => { setAddress(currentAddress || ''); }, [currentAddress]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (address.trim() && address !== currentAddress) {
      onSetAddress(chain.key, address.trim());
    }
  };

  const isChanged = address !== currentAddress;
  const isValid = address ? chain.pattern.test(address) : true;

  return (
    <div style={{ padding: '1.5rem', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--black)', transition: 'all 0.3s ease' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
        <h3 style={{ color: 'var(--text-light)', margin: 0, fontSize: '1.1rem' }}>{chain.name}</h3>
        <span style={{ background: currentAddress ? 'var(--gold)' : 'var(--text-secondary)', color: 'var(--black)', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 'bold' }}>
          {currentAddress ? 'Linked' : 'Not Set'}
        </span>
      </div>

      <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', marginBottom: '1rem', minHeight: '2.5rem' }}>{chain.description}</p>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={chain.placeholder}
            disabled={isSetting || !ensName || !derivedWalletReady}
            style={{
              width: '100%',
              padding: '0.75rem',
              background: 'var(--bg-dark)',
              border: `1px solid ${isValid ? 'var(--border)' : '#ff6b6b'}`,
              borderRadius: '4px',
              color: 'var(--text-light)',
              fontSize: '0.9rem',
              fontFamily: 'monospace'
            }}
          />
          {address && !isValid && <p style={{ color: '#ff6b6b', fontSize: '0.75rem', margin: '0.25rem 0 0 0' }}>Invalid {chain.name} address format</p>}
        </div>

        <button 
          type="submit" 
          disabled={!isChanged || !isValid || isSetting || !ensName || !derivedWalletReady} 
          className="btn btn-gold"
          style={{ 
            width: '100%', 
            opacity: (!isChanged || !isValid || isSetting || !ensName || !derivedWalletReady) ? 0.6 : 1, 
            cursor: (!isChanged || !isValid || isSetting || !ensName || !derivedWalletReady) ? 'not-allowed' : 'pointer' 
          }}
        >
          {isSetting ? 'Setting...' : currentAddress ? 'Update Address' : 'Link Address'}
        </button>
      </form>

      {currentAddress && (
        <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(255, 215, 0, 0.05)', borderRadius: '4px', border: '1px solid rgba(255, 215, 0, 0.2)' }}>
          <p style={{ color: 'var(--gold)', fontSize: '0.8rem', margin: 0, wordBreak: 'break-all', fontFamily: 'monospace' }}>
            Current: {currentAddress}
          </p>
        </div>
      )}
    </div>
  );
};

export default AddressLinker;
